import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { toast } from 'sonner'
import { frappeList } from '@/lib/erp/utils'
import { getERPClient } from '@/lib/erp/client'
import { useAuthStore } from '@/lib/store/authStore'

type ItemAttributeFilter = {
  name: string
  attribute_name: string
  show_on_storefront_filter: 0 | 1
  numeric_values: 0 | 1
}

const FILTER_ATTRS_KEY = ['filter-attribute-settings'] as const

function stripCompanySuffix(value: string, abbr: string | null): string {
  if (!abbr) return value
  const suffix = ` - ${abbr}`
  return value.endsWith(suffix) ? value.slice(0, -suffix.length) : value
}

function Divider() {
  return <div className="border-t border-border mx-4" />
}

export function FilterAttributeSettings() {
  const tenantCompany = useAuthStore((s) => s.tenantCompany)
  const queryClient = useQueryClient()

  const { data: companyAbbr } = useQuery({
    queryKey: ['company-abbr', tenantCompany],
    queryFn: async (): Promise<string | null> => {
      if (!tenantCompany) return null
      const rows = await frappeList<{ abbr: string }>('Company', {
        name: tenantCompany,
      }, ['abbr'])
      return rows[0]?.abbr ?? null
    },
    enabled: !!tenantCompany,
    staleTime: 30 * 60 * 1000,
  })

  const {
    data: attributes,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [...FILTER_ATTRS_KEY, tenantCompany ?? ''],
    queryFn: async (): Promise<ItemAttributeFilter[]> => {
      const client = getERPClient()
      return client.list<ItemAttributeFilter>({
        doctype: 'Item Attribute',
        filters: {
          disabled: 0,
          numeric_values: 0,
          ...(tenantCompany ? { company: tenantCompany } : {}),
        },
        fields: ['name', 'attribute_name', 'show_on_storefront_filter', 'numeric_values'],
        limit_page_length: 200,
      })
    },
    enabled: !!tenantCompany,
    staleTime: 60 * 1000,
  })

  const toggleMutation = useMutation({
    mutationFn: async ({ name, checked }: { name: string; checked: boolean }) => {
      const client = getERPClient()
      await client.update({
        doctype: 'Item Attribute',
        name,
        data: { show_on_storefront_filter: checked ? 1 : 0 },
      })
    },
    onMutate: async ({ name, checked }) => {
      await queryClient.cancelQueries({ queryKey: FILTER_ATTRS_KEY })
      const previous = queryClient.getQueryData<ItemAttributeFilter[]>([...FILTER_ATTRS_KEY, tenantCompany ?? ''])

      queryClient.setQueryData<ItemAttributeFilter[] | undefined>(
        [...FILTER_ATTRS_KEY, tenantCompany ?? ''],
        (old) =>
          old?.map((a) =>
            a.name === name
              ? { ...a, show_on_storefront_filter: checked ? 1 : 0 }
              : a,
          ),
      )

      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData([...FILTER_ATTRS_KEY, tenantCompany ?? ''], context.previous)
      }
      toast.error('Failed to save. Please try again.')
    },
    onSuccess: () => {
      toast('Filter setting saved.')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: FILTER_ATTRS_KEY })
    },
  })

  useEffect(() => {
    if (isError) {
      toast.error('Failed to load filter settings')
    }
  }, [isError])

  if (isLoading) {
    return (
      <Card className="rounded-2xl border-0">
        <CardContent className="p-0">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between px-4 py-5">
              <Skeleton className="w-32 h-4 rounded-md" />
              <Skeleton className="w-9 h-5 rounded-full" />
            </div>
          ))}
        </CardContent>
      </Card>
    )
  }

  if (!attributes || attributes.length === 0) {
    return (
      <Card className="rounded-2xl border-0">
        <CardContent className="px-4 py-5">
          <p className="text-sm text-muted-foreground">
            No product attributes found. Create attributes in Inventory to enable filters.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="rounded-2xl border-0">
      <CardContent className="p-0">
        {attributes.map((attr, idx) => (
          <div key={attr.name}>
            {idx > 0 && <Divider />}
            <div className="flex items-center justify-between px-4 py-5">
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium">
                  {stripCompanySuffix(attr.attribute_name, companyAbbr ?? null)}
                </span>
              </div>
              <Switch
                checked={!!attr.show_on_storefront_filter}
                onCheckedChange={(checked) =>
                  toggleMutation.mutate({ name: attr.name, checked })
                }
                disabled={toggleMutation.isPending}
                aria-label={`Show ${attr.attribute_name} as storefront filter`}
              />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
